import EvPermitZoneIcon from '@assets/jsx-icon/EvPermitZoneIcon';
import { CameraWithInfo } from '@EcamModel/model';
import { alpha, Box, Chip, Divider, Grid, Stack, Typography } from '@mui/material';
import {
    EvPermitZoneOption,
    formatPermitLength,
    formatZonesSummary,
    fromCameraEvPermitZones,
    groupZonesByLocation,
} from '@pages/cameras/utils/EvPermitHelpers';
import color from '@theme/Colors';
import { Fragment } from 'react/jsx-runtime';

export const OFF_MESSAGE = 'Off — this camera creates no permits and a detected EV gets no exemption.';

/** Read-only "EV permit exemption" card on the camera details page. */
export default function EvPermitExemptionSummary({ camera }: { camera: CameraWithInfo }) {
    const enabled = !!camera.IsSendEVPermitEnabled;

    // Camera's own zone is always sent, so it is listed first alongside the extra zones.
    const primaryZone: EvPermitZoneOption | undefined = camera.ZoneId
        ? {
              Id: camera.ZoneId,
              PublicName: camera.Zone?.PublicName ?? camera.ZoneName ?? '',
              LocationId: Number(camera.Zone?.LocationId),
              LocationName: camera.Zone?.Location?.Name ?? camera.LocationName ?? '',
          }
        : undefined;
    const extraZones = fromCameraEvPermitZones(camera.EvPermitZones).filter((z) => z.Id !== camera.ZoneId);
    const zones = primaryZone ? [primaryZone, ...extraZones] : extraZones;

    const statusColor = enabled ? color.success : color.grey600;

    return (
        <Grid item xs={12} bgcolor={color.white} padding={2} borderRadius={2}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                borderBottom="1px solid #E4E7EC"
                pb={1}
                mb={2}
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography color={color.darkPrimary} variant="h5">
                        EV permit exemption
                    </Typography>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        sx={{
                            display: 'inline-flex',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 999,
                            bgcolor: alpha(statusColor, 0.1),
                        }}
                    >
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusColor }} />
                        <Typography variant="body1" color={statusColor} lineHeight={1}>
                            {enabled ? 'On' : 'Off'}
                        </Typography>
                    </Stack>
                </Stack>
                {enabled && (
                    <Typography variant="body1">
                        {formatZonesSummary(zones)
                            .split(/(\d+)/)
                            .map((part, i) =>
                                i % 2 === 1 ? (
                                    <Box key={i} component="span" fontWeight={500}>
                                        {part}
                                    </Box>
                                ) : (
                                    part
                                )
                            )}
                    </Typography>
                )}
            </Stack>

            {!enabled ? (
                <Typography variant="body1">{OFF_MESSAGE}</Typography>
            ) : (
                <Stack spacing={1.5}>
                    <Box
                        display="grid"
                        gridTemplateColumns="max-content 1fr"
                        columnGap={1}
                        rowGap={1.5}
                        alignItems="flex-start"
                    >
                        {groupZonesByLocation(zones).map((group) => (
                            <Fragment key={group.locationName}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <EvPermitZoneIcon />
                                    <Typography
                                        variant="body1"
                                        color={color.grey600}
                                        fontWeight={500}
                                        whiteSpace="nowrap"
                                    >
                                        {group.locationName}:
                                    </Typography>
                                </Stack>
                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                    {group.zones.map((zone) => (
                                        <Chip
                                            key={zone.Id}
                                            label={zone.PublicName}
                                            size="small"
                                            sx={{
                                                bgcolor: '#F8FAFC',
                                                borderRadius: '6px',
                                                fontWeight: 400,
                                                fontSize: 14,
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Fragment>
                        ))}
                    </Box>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body1">Permit length:</Typography>
                        <Typography variant="body1">{formatPermitLength(camera.EvPermitLengthSeconds)}</Typography>
                    </Stack>
                </Stack>
            )}
        </Grid>
    );
}
